import React, { useEffect, useState } from "react";
import { Wallet, ShieldCheck, Home, Users, LineChart, Building2, Plus, Trash2, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/hooks/useSeo";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

// -----------------------------------------------------------------------------
// NetWorth — Policies, Property, Debtors, Equity (FIFO), Business shares.
// Auto-computes total net worth + generates a signed-format PDF certificate.
// -----------------------------------------------------------------------------
const TABS = [
  { k:"summary",    l:"Summary",    Icon:LineChart  },
  { k:"policies",   l:"Policies",   Icon:ShieldCheck },
  { k:"property",   l:"Property",   Icon:Home        },
  { k:"debtors",    l:"Debtors",    Icon:Users       },
  { k:"equity",     l:"Equity",     Icon:LineChart   },
  { k:"businesses", l:"Businesses", Icon:Building2   },
];

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function NetWorth() {
  const { user } = useAuth();
  const [tab, setTab] = useState("summary");
  useSeo({ title:"Net Worth · Certificate · Policies · Property · Equity · find2near",
           description:"Track your policies, land, shops, equity (FIFO), debtors and business shareholdings. Auto-generates a PDF Net Worth Certificate.", canonical:"/networth" });

  if (!user) return <div className="max-w-md mx-auto text-center py-24 px-6"><Wallet className="mx-auto text-emerald-700" size={44}/><h2 className="heading text-2xl font-black mt-4">Sign in to open Net Worth</h2></div>;

  const downloadCert = () => {
    // Session-cookie backed request. Just open the URL — the browser handles auth.
    window.open(`${BACKEND}/api/networth/certificate.pdf`, "_blank");
  };

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-6 py-6" data-testid="networth-page">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-stone-500 font-bold flex items-center gap-2"><Wallet size={12} className="text-emerald-700"/> Net worth</div>
          <h1 className="heading text-3xl md:text-4xl font-black text-stone-900">Every asset. <span className="text-emerald-700">One certificate.</span></h1>
        </div>
        <Button onClick={downloadCert} className="rounded-full bg-emerald-600 hover:bg-emerald-700 font-black" data-testid="nw-download-pdf"><Download size={13} className="mr-1.5"/> Certificate PDF</Button>
      </div>
      <div className="flex gap-1.5 mb-4 flex-wrap">
        {TABS.map(({k,l,Icon})=>(
          <button key={k} onClick={()=>setTab(k)} className={`text-xs font-black rounded-full px-3 py-1.5 inline-flex items-center gap-1.5 border ${tab===k?"bg-stone-900 text-white border-stone-900":"bg-white text-stone-700 border-stone-200"}`} data-testid={`nw-tab-${k}`}><Icon size={12}/> {l}</button>
        ))}
      </div>
      {tab==="summary"    && <Summary/>}
      {tab==="policies"   && <Registry kind="policies"   endpoint="/networth/policies"   fields={POLICY_FIELDS}/>}
      {tab==="property"   && <Registry kind="property"   endpoint="/networth/property"   fields={PROP_FIELDS}/>}
      {tab==="debtors"    && <Registry kind="debtors"    endpoint="/networth/debtors"    fields={DEBT_FIELDS}/>}
      {tab==="equity"     && <Registry kind="equity"     endpoint="/networth/equity"     fields={EQ_FIELDS}/>}
      {tab==="businesses" && <Registry kind="businesses" endpoint="/networth/businesses" fields={BIZ_FIELDS}/>}
    </div>
  );
}

function Summary() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/networth/summary").then(r=>setS(r.data)).catch(()=>{}); }, []);
  if (!s) return <div className="text-center py-16 text-stone-400"><Loader2 className="mx-auto animate-spin"/></div>;
  const a = s.assets;
  return (
    <div className="space-y-3">
      <div className="rounded-3xl bg-gradient-to-br from-emerald-500 to-teal-700 text-white p-6" data-testid="nw-summary">
        <div className="text-[11px] uppercase tracking-widest font-black opacity-70">Estimated net worth</div>
        <div className="text-4xl md:text-5xl font-black font-mono mt-1">₹ {Math.round(s.net_worth).toLocaleString("en-IN")}</div>
        <div className="text-xs opacity-80 mt-1">Across {Object.values(s.counts).reduce((a,b)=>a+b,0)} entries · liabilities not yet tracked</div>
      </div>
      <div className="grid gap-2 md:grid-cols-3">
        {Object.entries(a).map(([k,v]) => (
          <div key={k} className="rounded-2xl bg-white border border-stone-200 p-3">
            <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500">{k.replace(/_/g," ")}</div>
            <div className="font-mono font-black text-stone-900 mt-1">₹ {Math.round(v).toLocaleString("en-IN")}</div>
          </div>
        ))}
      </div>
      {s.renewals_due_45d?.length > 0 && (
        <div className="rounded-3xl bg-amber-50 border border-amber-200 p-4" data-testid="nw-renewals">
          <div className="text-[11px] uppercase tracking-widest font-bold text-amber-800 mb-1">🔔 Policy renewals due in 45 days</div>
          <ul className="text-sm text-amber-900 space-y-0.5">
            {s.renewals_due_45d.map(p => <li key={p.id}><b>{p.policy_no}</b> · {p.provider} · due {p.renewal_date}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

function Registry({ kind, endpoint, fields }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const load = () => api.get(endpoint).then(r=>setItems(r.data)).catch(()=>{});
  useEffect(load, [endpoint]);
  const submit = async () => {
    const missing = fields.filter(f=>f.required && !form[f.key]).map(f=>f.label);
    if (missing.length) { toast.error("Required: "+missing.join(", ")); return; }
    setBusy(true);
    try {
      const payload = {}; fields.forEach(f => {
        const v = form[f.key];
        payload[f.key] = f.type === "number" ? (v==="" || v==null ? null : parseFloat(v)) : (v==="" ? null : v);
      });
      await api.post(endpoint, payload);
      toast.success("Added"); load(); setForm({});
    } catch (e) { toast.error(e?.response?.data?.detail||"Failed"); } finally { setBusy(false); }
  };
  const del = async (id) => { await api.delete(`${endpoint}/${id}`); load(); };

  return (
    <div className="space-y-3">
      <div className="rounded-3xl bg-white border border-stone-200 p-4" data-testid={`nw-form-${kind}`}>
        <div className="text-[11px] uppercase tracking-widest font-bold text-stone-500 mb-2">Add new</div>
        <div className="grid gap-2 md:grid-cols-3">
          {fields.map(f => (
            <Input key={f.key} type={f.type || "text"} placeholder={f.label + (f.required?" *":"")}
                   value={form[f.key] ?? ""} onChange={e=>setForm({...form, [f.key]: e.target.value})}
                   className="rounded-2xl h-10" data-testid={`nw-${kind}-${f.key}`}/>
          ))}
        </div>
        <Button onClick={submit} disabled={busy} className="mt-2 rounded-full bg-emerald-600 hover:bg-emerald-700 font-black">{busy && <Loader2 className="animate-spin mr-2" size={13}/>}<Plus size={13} className="mr-1"/> Add</Button>
      </div>
      <div className="rounded-3xl bg-white border border-stone-200 divide-y divide-stone-100">
        {items.length===0 ? <div className="p-6 text-center text-stone-400 text-sm">Nothing here yet.</div>
          : items.map(x => (
            <div key={x.id} className="p-3 flex items-center gap-3 text-sm">
              <div className="flex-1 min-w-0">
                <div className="font-black text-stone-900">
                  {x.policy_no || x.symbol || x.business_name || x.name || (x.kind + " · " + (x.address||"—")).slice(0,60)}
                </div>
                <div className="text-[11px] text-stone-500 font-mono">
                  {[x.provider, x.buy_date, x.city, x.location, x.nvn, x.qty && `${x.qty} @ ₹${x.buy_price}`,
                    x.sum_insured && `₹${Number(x.sum_insured).toLocaleString()}`,
                    x.amount_due && `Due ₹${Number(x.amount_due).toLocaleString()}`,
                    x.current_value && `Value ₹${Number(x.current_value).toLocaleString()}`,
                    x.my_share_pct && `${x.my_share_pct}% share`,
                  ].filter(Boolean).join(" · ")}
                </div>
              </div>
              <button onClick={()=>del(x.id)} className="text-stone-400 hover:text-red-600" data-testid={`nw-del-${x.id}`}><Trash2 size={14}/></button>
            </div>
          ))}
      </div>
    </div>
  );
}

const POLICY_FIELDS = [
  { key:"policy_no",     label:"Policy no.",    required:true },
  { key:"provider",      label:"Provider",      required:true },
  { key:"kind",          label:"Kind (life/health/motor)" },
  { key:"holder",        label:"Holder name" },
  { key:"sum_insured",   label:"Sum insured (₹)", type:"number" },
  { key:"premium_annual",label:"Annual premium (₹)", type:"number" },
  { key:"start_date",    label:"Start (YYYY-MM-DD)" },
  { key:"maturity_date", label:"Maturity (YYYY-MM-DD)" },
  { key:"renewal_date",  label:"Next renewal (YYYY-MM-DD)" },
];
const PROP_FIELDS = [
  { key:"kind",           label:"Kind (land/shop/flat)", required:true },
  { key:"address",        label:"Address", required:true },
  { key:"city",           label:"City" },
  { key:"area_value",     label:"Area value", type:"number" },
  { key:"area_unit",      label:"Area unit (sqft/sqm/acre)" },
  { key:"purchase_date",  label:"Purchase date" },
  { key:"purchase_price", label:"Purchase price (₹)", type:"number" },
  { key:"current_value",  label:"Current value (₹)", type:"number" },
  { key:"ownership_pct",  label:"Ownership %", type:"number" },
  { key:"lat",            label:"Lat", type:"number" },
  { key:"lng",            label:"Lng", type:"number" },
];
const DEBT_FIELDS = [
  { key:"name",       label:"Name", required:true },
  { key:"amount_due", label:"Amount due (₹)", type:"number", required:true },
  { key:"nvn",        label:"NVN (12 digits)" },
  { key:"mobile",     label:"Mobile" },
  { key:"city",       label:"City" },
  { key:"address",    label:"Address" },
  { key:"since_date", label:"Since (YYYY-MM-DD)" },
];
const EQ_FIELDS = [
  { key:"symbol",     label:"Symbol (e.g. RELIANCE)", required:true },
  { key:"kind",       label:"Kind (long/short)" },
  { key:"qty",        label:"Qty", type:"number", required:true },
  { key:"buy_price",  label:"Buy price ₹", type:"number", required:true },
  { key:"buy_date",   label:"Buy date (YYYY-MM-DD)", required:true },
  { key:"sell_price", label:"Sell price ₹ (if sold)", type:"number" },
  { key:"sell_date",  label:"Sell date" },
];
const BIZ_FIELDS = [
  { key:"business_name",  label:"Business name", required:true },
  { key:"location",       label:"Location" },
  { key:"my_share_pct",   label:"My share %", type:"number" },
  { key:"stock_valuation",label:"Stock value ₹", type:"number" },
  { key:"asset_valuation",label:"Asset value ₹", type:"number" },
];
